import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { Item, Registry } from '@lds/shared'
import { registryApi, itemApi } from '../../lib/api'
import { t } from '../../lib/i18n'

function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
}

function fmtEuroCents(cents: number): string {
  const euros = cents / 100
  return `${Math.round(euros)}\u00a0€`
}

export default function RegistryDetail() {
  const { rid } = useParams<{ rid: string }>()
  const nav = useNavigate()
  const [reg, setReg] = useState<Registry | null>(null)
  const [items, setItems] = useState<Item[] | null>(null)
  const [busy, setBusy] = useState(false)

  const [statusOpen, setStatusOpen] = useState(false)
  const statusRef = useRef<HTMLDivElement | null>(null)

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [undo, setUndo] = useState<
    null | { kind: 'delete'; item: Item } | { kind: 'hide'; item: Item; wasHidden: boolean }
  >(null)
  const undoTimer = useRef<number | null>(null)
  const pendingDeleteTimer = useRef<number | null>(null)

  const refresh = useCallback(async () => {
    if (!rid) return
    const [r, its] = await Promise.all([registryApi.get(rid), itemApi.list(rid)])
    setReg(r)
    setItems(its)
  }, [rid])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    if (!statusOpen) return
    const onDoc = (e: MouseEvent) => {
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) setStatusOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setStatusOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [statusOpen])

  useEffect(() => {
    return () => {
      if (undoTimer.current) window.clearTimeout(undoTimer.current)
      if (pendingDeleteTimer.current) window.clearTimeout(pendingDeleteTimer.current)
    }
  }, [])

  if (!rid || !reg || !items) return <main style={{ padding: '2rem' }}>Chargement…</main>

  const ordered = [...items].sort((a, b) => a.sortOrder - b.sortOrder)

  function showUndo(msg: NonNullable<typeof undo>) {
    setUndo(msg)
    if (undoTimer.current) window.clearTimeout(undoTimer.current)
    undoTimer.current = window.setTimeout(() => setUndo(null), 5000)
  }

  async function togglePublish() {
    if (!rid || busy || !reg) return
    setBusy(true)
    try {
      if (reg.status === 'draft') await registryApi.publish(rid)
      else await registryApi.unpublish(rid)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function move(idx: number, dir: -1 | 1) {
    if (!rid || busy) return
    const j = idx + dir
    if (j < 0 || j >= ordered.length) return
    const next = [...ordered]
    const tmp = next[idx]
    next[idx] = next[j]
    next[j] = tmp
    setItems(next)
    setBusy(true)
    try {
      await itemApi.reorder(
        rid,
        next.map((i) => i.id),
      )
      await refresh()
    } catch {
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function toggleHide(it: Item) {
    if (!rid || busy) return
    const wasHidden = it.hidden
    setItems((arr) =>
      arr ? arr.map((x) => (x.id === it.id ? { ...x, hidden: !x.hidden } : x)) : arr,
    )
    showUndo({ kind: 'hide', item: it, wasHidden })
    try {
      await itemApi.update(rid, it.id, { hidden: !wasHidden })
    } catch {
      await refresh()
    }
  }

  async function toggleOffered(it: Item) {
    if (!rid || busy) return
    const wasOffered = it.offered
    setItems((arr) =>
      arr ? arr.map((x) => (x.id === it.id ? { ...x, offered: !x.offered } : x)) : arr,
    )
    try {
      await itemApi.update(rid, it.id, { offered: !wasOffered })
    } catch {
      await refresh()
    }
  }

  function askDelete(id: string) {
    setConfirmDelete(id)
  }

  function doDelete() {
    if (!rid || !confirmDelete) return
    const it = ordered.find((x) => x.id === confirmDelete) ?? null
    setItems((arr) => (arr ? arr.filter((x) => x.id !== confirmDelete) : arr))
    setConfirmDelete(null)
    if (it) showUndo({ kind: 'delete', item: it })

    if (pendingDeleteTimer.current) window.clearTimeout(pendingDeleteTimer.current)
    pendingDeleteTimer.current = window.setTimeout(async () => {
      try {
        await itemApi.delete(rid, it?.id ?? confirmDelete)
      } finally {
        pendingDeleteTimer.current = null
      }
    }, 5000)
  }

  async function undoAction() {
    if (!rid || !undo) return
    if (undo.kind === 'delete') {
      if (pendingDeleteTimer.current) window.clearTimeout(pendingDeleteTimer.current)
      pendingDeleteTimer.current = null
      setItems((arr) => (arr ? [...arr, undo.item] : [undo.item]))
    } else if (undo.kind === 'hide') {
      setItems((arr) =>
        arr ? arr.map((x) => (x.id === undo.item.id ? { ...x, hidden: undo.wasHidden } : x)) : arr,
      )
      try {
        await itemApi.update(rid, undo.item.id, { hidden: undo.wasHidden })
      } catch {
        await refresh()
      }
    }
    setUndo(null)
  }

  return (
    <div className="owner-registry-detail rd-page">
      <div className="rd-topbar">
        <div className="rd-topbar-inner">
          <Link to="/me" className="rd-back" aria-label="Retour à mes listes">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M15 6 L9 12 L15 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="rd-back-label">Mes listes</span>
          </Link>

          <div className="rd-title-block">
            <h1 className="rd-title">{reg.title}</h1>
            {reg.dueDate && (
              <p className="rd-due">
                <em>Naissance prévue le</em> {fmtDate(reg.dueDate)}
              </p>
            )}
          </div>

          <div className="rd-actions">
            <div className="rd-status-wrap" ref={statusRef}>
              <button
                type="button"
                className={`rd-pill ${reg.status === 'published' ? 'rd-pill-on' : 'rd-pill-draft'}`}
                aria-haspopup="menu"
                aria-expanded={statusOpen}
                onClick={() => setStatusOpen((v) => !v)}
              >
                <span className="rd-pill-dot" aria-hidden="true" />
                <span>{reg.status === 'published' ? 'Publié' : 'Brouillon'}</span>
                <span className="rd-pill-caret" aria-hidden="true">
                  ▾
                </span>
              </button>
              {statusOpen && (
                <div className="rd-pop" role="menu">
                  {reg.status === 'draft' ? (
                    <button
                      type="button"
                      role="menuitem"
                      className="rd-pop-item"
                      onClick={() => {
                        setStatusOpen(false)
                        void togglePublish()
                      }}
                    >
                      <span className="rd-pop-icon rd-pop-icon-on" aria-hidden="true" />
                      <span>
                        <span className="rd-pop-title">Publier maintenant</span>
                        <span className="rd-pop-sub">La liste devient accessible aux invités.</span>
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      role="menuitem"
                      className="rd-pop-item"
                      onClick={() => {
                        setStatusOpen(false)
                        void togglePublish()
                      }}
                    >
                      <span className="rd-pop-icon rd-pop-icon-draft" aria-hidden="true" />
                      <span>
                        <span className="rd-pop-title">Dépublier</span>
                        <span className="rd-pop-sub">
                          La liste cesse d&apos;être accessible aux invités.
                        </span>
                      </span>
                    </button>
                  )}
                  <div className="rd-pop-foot">
                    <code>/r/{reg.slug}</code>
                  </div>
                </div>
              )}
            </div>

            {reg.status === 'published' && (
              <Link
                to={`/r/${reg.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rd-see-list-btn"
              >
                {t('owner.registry.see_list')}
              </Link>
            )}

            <Link
              to={`/me/r/${rid}/settings`}
              className="rd-icon-btn"
              aria-label="Réglages de la liste"
              title="Réglages"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.4" />
                <path
                  d="M12 3v2.2M12 18.8V21M3 12h2.2M18.8 12H21M5.6 5.6l1.6 1.6M16.8 16.8l1.6 1.6M5.6 18.4l1.6-1.6M16.8 7.2l1.6-1.6"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      <div className="rd-body">
        {ordered.length === 0 ? (
          <div className="empty-stage">
            <div className="empty-illus" aria-hidden="true">
              <svg viewBox="0 0 200 140" width="200" height="140">
                <line
                  x1="20"
                  y1="118"
                  x2="180"
                  y2="118"
                  stroke="currentColor"
                  strokeOpacity=".18"
                  strokeWidth="1"
                  strokeDasharray="2 5"
                />
                <g transform="translate(100, 70)">
                  <rect
                    x="-44"
                    y="-30"
                    width="88"
                    height="60"
                    rx="3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.2"
                  />
                  <path
                    d="M -44 -22 L -16 -22 M -44 -10 L 16 -10 M -44 2 L -4 2 M -44 14 L 30 14"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeOpacity=".5"
                    strokeLinecap="round"
                  />
                </g>
                <g transform="translate(100, 30)" opacity=".55">
                  <circle cx="-10" cy="0" r="2" fill="currentColor" />
                  <line x1="-4" y1="0" x2="4" y2="0" stroke="currentColor" strokeWidth="1" />
                  <circle cx="10" cy="0" r="2" fill="currentColor" />
                </g>
              </svg>
            </div>
            <h2 className="empty-title">Aucun cadeau pour l&apos;instant.</h2>
            <p className="empty-sub">
              Commencez par ajouter quelques cadeaux. Vous pourrez ensuite publier votre liste pour
              la partager.
            </p>
            <button
              type="button"
              className="btn-primary btn-lg"
              onClick={() => nav(`/me/r/${rid}/items/new`)}
            >
              <span aria-hidden="true" style={{ marginRight: 4 }}>
                +
              </span>{' '}
              Ajouter votre premier cadeau
            </button>
          </div>
        ) : (
          <>
            <div className="rd-section-head">
              <div>
                <p className="eyebrow">Cadeaux</p>
                <h2 className="rd-section-title">
                  {ordered.length} {ordered.length > 1 ? 'cadeaux' : 'cadeau'}{' '}
                  <span className="muted">· glissez les flèches pour réordonner</span>
                </h2>
              </div>
              <button
                type="button"
                className="btn-primary rd-add-desktop"
                onClick={() => nav(`/me/r/${rid}/items/new`)}
              >
                <span aria-hidden="true">+</span> Ajouter un cadeau
              </button>
            </div>

            <ul className="rd-list" role="list">
              {ordered.map((it, idx) => {
                const pct = Math.min(
                  100,
                  Math.round((it.alreadyGivenCents / Math.max(1, it.priceCents)) * 100),
                )
                const restant = Math.max(0, it.priceCents - it.alreadyGivenCents)
                const complet = restant <= 0
                return (
                  <li key={it.id} className={`rd-row ${it.hidden ? 'rd-row-hidden' : ''}`}>
                    <div
                      className="rd-reorder"
                      aria-label={`Position ${idx + 1} sur ${ordered.length}`}
                    >
                      <button
                        type="button"
                        className="rd-reorder-btn"
                        aria-label="Monter"
                        disabled={busy || idx === 0}
                        onClick={() => void move(idx, -1)}
                      >
                        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                          <path
                            d="M3 10 L8 5 L13 10"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <span className="rd-reorder-num" aria-hidden="true">
                        {idx + 1}
                      </span>
                      <button
                        type="button"
                        className="rd-reorder-btn"
                        aria-label="Descendre"
                        disabled={busy || idx === ordered.length - 1}
                        onClick={() => void move(idx, 1)}
                      >
                        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                          <path
                            d="M3 6 L8 11 L13 6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="rd-thumb">
                      {it.imageUrl ? (
                        <img src={it.imageUrl} alt="" loading="lazy" />
                      ) : (
                        <div className="rd-thumb-placeholder" aria-hidden="true">
                          <svg viewBox="0 0 40 40" width="28" height="28">
                            <rect
                              x="6"
                              y="9"
                              width="28"
                              height="22"
                              rx="2"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.2"
                            />
                            <path
                              d="M10 26 L17 19 L23 24 L28 20 L34 26"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.2"
                              strokeLinejoin="round"
                            />
                            <circle cx="15" cy="15" r="1.6" fill="currentColor" />
                          </svg>
                        </div>
                      )}
                      {it.hidden && <span className="rd-thumb-tag">Masqué</span>}
                      {it.offered && (
                        <span className="rd-thumb-tag rd-thumb-tag-offered">Offert</span>
                      )}
                    </div>

                    <div className="rd-row-body">
                      <div className="rd-row-title-row">
                        <h3 className="rd-row-title">{it.title}</h3>
                        <span className="rd-row-price">{fmtEuroCents(it.priceCents)}</span>
                      </div>
                      <div className="rd-row-progress">
                        <div className="rd-prog" aria-hidden="true">
                          <div className="rd-prog-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="rd-row-meta">
                          {it.offered ? (
                            <span className="rd-row-meta-done">Marqué comme offert 🎁</span>
                          ) : complet ? (
                            <span className="rd-row-meta-done">Entièrement offert ❤️</span>
                          ) : it.alreadyGivenCents > 0 ? (
                            <span>
                              <strong>{fmtEuroCents(it.alreadyGivenCents)}</strong>{' '}
                              <span className="muted">déjà offerts ·</span> reste{' '}
                              <strong>{fmtEuroCents(restant)}</strong>
                            </span>
                          ) : (
                            <span className="muted">
                              Aucune participation · {fmtEuroCents(it.priceCents)} à offrir
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="rd-row-actions">
                      <button
                        type="button"
                        className={`rd-icon-btn rd-icon-btn-sm ${it.offered ? 'rd-icon-btn-on' : ''}`}
                        aria-label={
                          it.offered ? 'Marquer comme non offert' : 'Marquer comme offert'
                        }
                        aria-pressed={it.offered}
                        title={
                          it.offered ? 'Offert — cliquez pour réactiver' : 'Marquer comme offert'
                        }
                        onClick={() => void toggleOffered(it)}
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                          <path
                            d="M4 11h16v9H4z M3 7h18v4H3z M12 7v13 M12 7S10 3 7.5 3 5 5.5 7 7 M12 7s2-4 4.5-4 2.5 2.5.5 4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>

                      <button
                        type="button"
                        className="rd-icon-btn rd-icon-btn-sm"
                        aria-label="Modifier"
                        title="Modifier"
                        onClick={() => nav(`/me/r/${rid}/items/${it.id}`)}
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                          <path
                            d="M4 20h4l10-10-4-4L4 16v4Z M14 6l4 4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>

                      <button
                        type="button"
                        className="rd-icon-btn rd-icon-btn-sm"
                        aria-label={it.hidden ? 'Réafficher' : 'Masquer'}
                        title={it.hidden ? 'Réafficher' : 'Masquer'}
                        onClick={() => void toggleHide(it)}
                      >
                        {it.hidden ? (
                          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                            <path
                              d="M3 12s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7Z M4 4l16 16"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <circle
                              cx="12"
                              cy="12"
                              r="2.6"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                            />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                            <path
                              d="M3 12s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7Z"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                            />
                            <circle
                              cx="12"
                              cy="12"
                              r="2.8"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                            />
                          </svg>
                        )}
                      </button>

                      <button
                        type="button"
                        className="rd-icon-btn rd-icon-btn-sm rd-icon-btn-danger"
                        aria-label="Supprimer"
                        title="Supprimer"
                        onClick={() => askDelete(it.id)}
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                          <path
                            d="M5 7h14 M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2 M7 7l1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13 M10 11v6 M14 11v6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </div>

      {ordered.length > 0 && (
        <button
          type="button"
          className="rd-fab"
          aria-label="Ajouter un cadeau"
          onClick={() => nav(`/me/r/${rid}/items/new`)}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}

      {confirmDelete && (
        <div
          className="rd-overlay"
          onClick={() => setConfirmDelete(null)}
          role="presentation"
          aria-hidden="true"
        >
          <div
            className="rd-confirm"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rd-conf-title"
          >
            <p className="eyebrow">Supprimer définitivement</p>
            <h3 id="rd-conf-title" className="rd-confirm-title">
              Supprimer ce cadeau&nbsp;?
            </h3>
            <p className="rd-confirm-sub">
              Cette action est irréversible. Les participations associées seront archivées mais
              restent visibles dans votre historique.
            </p>
            <div className="rd-confirm-foot">
              <button type="button" className="btn-ghost" onClick={() => setConfirmDelete(null)}>
                Annuler
              </button>
              <button type="button" className="btn-danger" onClick={doDelete}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {undo && (
        <div className="rd-toast" role="status" aria-live="polite">
          <span>
            {undo.kind === 'delete' && (
              <>
                <em>{undo.item.title}</em> supprimé.
              </>
            )}
            {undo.kind === 'hide' &&
              (undo.wasHidden ? (
                <>
                  <em>{undo.item.title}</em> réaffiché.
                </>
              ) : (
                <>
                  <em>{undo.item.title}</em> masqué pour les invités.
                </>
              ))}
          </span>
          <button type="button" className="rd-toast-undo" onClick={() => void undoAction()}>
            Annuler
          </button>
        </div>
      )}
    </div>
  )
}
